import { useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { FullScreenDialog } from '@/components/ui/full-screen-dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import OrganizationChart from 'components/IncidentConfig/OrganizationChart';
import {
  RosterDraftProvider,
  useCreateRosterDraft,
} from 'components/IncidentConfig/CreateIncident/CreateRosterDraftContext';
import RosterActions from 'components/IncidentConfig/roster/RosterActions';
import type {
  DraftRosterAssignment,
  DraftRosterSeat,
} from 'components/IncidentConfig/CreateIncident/createIncidentModel';
import {
  useSaveFilledRosterTemplate,
  type FilledRosterAssignment,
  type FilledRosterResourceLink,
  type FilledRosterTemplate,
} from 'hooks/rosterTemplateLibrary/filledRosterTemplateHooks';
import { selectGroupGuid } from 'slices/commonSelectors';

import {
  draftAssignmentsFromFilled,
  filledAssignmentsFromDraft,
} from './filledRosterAssignments';
import {
  duplicateSeatNames,
  seatsFromNodes,
  type TemplateEditorSeed,
} from './rosterTemplateSeats';

export type FilledTemplateEditorSeed = TemplateEditorSeed & {
  sourceTemplateId: string | null;
  sourceSlug: string | null;
};

export function FilledRosterTemplateEditorDialog({
  existing,
  seed,
  onClose,
}: {
  existing: FilledRosterTemplate | null;
  seed: FilledTemplateEditorSeed;
  onClose: () => void;
}) {
  const groupGuid = useSelector(selectGroupGuid);
  const saveTemplate = useSaveFilledRosterTemplate();

  const [name, setName] = useState(existing?.name ?? seed.name);
  const [seats, setSeats] = useState<DraftRosterSeat[]>(() =>
    seatsFromNodes(seed.nodes)
  );
  const [resources, setResources] = useState<
    (FilledRosterResourceLink & { positionId: string })[]
  >(() => {
    const initialSeats = seatsFromNodes(seed.nodes);
    return (existing?.resources ?? []).flatMap((resource) => {
      const positionId = initialSeats.find(
        (seat) => seat.name.trim() === resource.seatName.trim()
      )?.id;
      return positionId ? [{ ...resource, positionId }] : [];
    });
  });
  const [assignments, setAssignments] = useState<DraftRosterAssignment[]>(() =>
    draftAssignmentsFromFilled(
      seatsFromNodes(seed.nodes),
      existing?.assignments ?? []
    )
  );
  const preservedContacts = useMemo(
    () =>
      (existing?.assignments ?? []).filter(
        (row) => row.personKind === 'contact' || !row.userGuid
      ),
    [existing?.assignments]
  );

  const handleChange = useCallback(
    (
      nextSeats: DraftRosterSeat[],
      nextAssignments: DraftRosterAssignment[]
    ) => {
      setSeats(nextSeats);
      setAssignments(nextAssignments);
    },
    []
  );

  const duplicates = useMemo(() => duplicateSeatNames(seats), [seats]);
  const hasBlankSeat = seats.some((seat) => !seat.name.trim());
  const saving = saveTemplate.isPending;
  const canSave =
    !!name.trim() &&
    seats.length > 0 &&
    !hasBlankSeat &&
    duplicates.length === 0 &&
    !saving &&
    Boolean(seed.sourceTemplateId || seed.sourceSlug);

  const handleSave = () => {
    const payload = {
      id: existing?.id,
      name: name.trim(),
      sourceTemplateId: seed.sourceTemplateId,
      sourceSlug: seed.sourceSlug,
      assignments: filledAssignmentsFromDraft(
        seats,
        assignments,
        preservedContacts
      ) as FilledRosterAssignment[],
      resources: resources.flatMap((resource) => {
        const seatName =
          seats.find((seat) => seat.id === resource.positionId)?.name.trim() ||
          resource.seatName.trim();
        if (!seatName) return [];
        return [
          {
            seatName,
            resourceSetId: resource.resourceSetId,
            name: resource.name,
          },
        ];
      }),
    };
    saveTemplate.mutate(payload, { onSuccess: onClose });
  };

  if (!groupGuid) return null;

  return (
    <RosterDraftProvider
      groupGuid={groupGuid}
      seats={seats}
      assignments={assignments}
      creator={null}
      structureOnly={false}
      allowAssign
      templateAssignment
      templateName={name}
      templateResources={resources}
      onAddTemplateResource={(resource) =>
        setResources((current) => [
          ...current,
          {
            positionId: resource.positionId,
            resourceSetId: resource.resourceSetId,
            name: resource.name,
            seatName:
              seats
                .find((seat) => seat.id === resource.positionId)
                ?.name.trim() || '',
          },
        ])
      }
      onChange={handleChange}
    >
      <FullScreenDialog
        open
        onOpenChange={(next) => {
          if (!next) onClose();
        }}
        title={
          existing ? `Edit ${existing.name}` : 'New filled roster template'
        }
        description="Build the chart and assign one or more people to each seat. Assignments are saved with this template."
        bodyClassName="tw:!flex tw:!min-h-0 tw:!flex-col tw:!overflow-hidden tw:!p-3"
        headerLeading={
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </Button>
        }
        header={
          <div className="tw:flex tw:flex-col tw:gap-1 tw:md:max-w-md">
            <Label htmlFor="filled-roster-template-editor-name">Name</Label>
            <Input
              id="filled-roster-template-editor-name"
              value={name}
              maxLength={200}
              onChange={(event) => setName(event.target.value)}
            />
          </div>
        }
        footer={
          <>
            {duplicates.length > 0 ? (
              <p className="tw:mr-auto tw:m-0 tw:text-xs tw:text-destructive">
                Seat names must be unique. Rename: {duplicates.join(', ')}
              </p>
            ) : hasBlankSeat ? (
              <p className="tw:mr-auto tw:m-0 tw:text-xs tw:text-destructive">
                Every seat needs a name.
              </p>
            ) : !seed.sourceTemplateId && !seed.sourceSlug ? (
              <p className="tw:mr-auto tw:m-0 tw:text-xs tw:text-destructive">
                Start from an existing roster structure.
              </p>
            ) : null}
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={onClose}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              type="button"
              size="sm"
              disabled={!canSave}
              onClick={handleSave}
            >
              {saving ? 'Saving…' : 'Save template'}
            </Button>
          </>
        }
      >
        <FilledRosterChartBody />
      </FullScreenDialog>
    </RosterDraftProvider>
  );
}

function FilledRosterChartBody() {
  const { members } = useCreateRosterDraft();
  return (
    <>
      <RosterActions hideAnalytics />
      <div className="tw:min-h-0 tw:flex-1 tw:overflow-auto">
        <OrganizationChart incidentMembers={members} />
      </div>
    </>
  );
}
